import { forwardRef, useCallback } from "react";
import { cn } from "@/lib/cn";

/**
 * Slider — single-value range slider (Faza 8 polish).
 *
 * Native input[type=range] ustiga custom track + thumb (CSS only).
 * Aurora gradient range, violet thumb, focus glow.
 *
 * Token'lar: --slider-* (tokens.css)
 *
 * Use-case: confidence threshold, retention days, max tokens.
 *
 * A11y:
 *   - Native input semantik (role="slider")
 *   - aria-label/labelledby caller'dan
 *   - Keyboard: ←→ ±step, Home/End min/max
 */
export interface SliderProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "defaultValue"> {
  value?: number;
  defaultValue?: number;
  onValueChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Qiymatni o'ng tomonda inline ko'rsatish */
  showValue?: boolean;
  /** Suffix (masalan "kun", "%") */
  valueSuffix?: string;
  /** Format funksiyasi (showValue uchun) */
  formatValue?: (v: number) => string;
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      className,
      value,
      defaultValue,
      onValueChange,
      min = 0,
      max = 100,
      step = 1,
      showValue = false,
      valueSuffix,
      formatValue,
      onChange,
      ...props
    },
    ref,
  ) => {
    const currentValue =
      typeof value === "number"
        ? value
        : typeof defaultValue === "number"
          ? defaultValue
          : min;
    const ratio = max === min ? 0 : (currentValue - min) / (max - min);
    const fillPct = `${Math.max(0, Math.min(1, ratio)) * 100}%`;

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = Number(e.target.value);
        onValueChange?.(v);
        onChange?.(e);
      },
      [onValueChange, onChange],
    );

    return (
      <div className={cn("flex items-center gap-3 w-full", className)}>
        <div className="relative flex-1 h-5 flex items-center group">
          {/* Track (background) */}
          <div
            aria-hidden
            className="absolute inset-x-0 h-1.5 rounded-full bg-[var(--slider-track-bg)]"
          />
          {/* Range (fill) */}
          <div
            aria-hidden
            className="absolute h-1.5 rounded-full"
            style={{
              width: fillPct,
              background: "var(--slider-range-bg)",
            }}
          />
          {/* Native range input — invisible but interactive */}
          <input
            ref={ref}
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            className="aurora-slider relative w-full h-5 appearance-none bg-transparent cursor-pointer focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            {...props}
          />
        </div>
        {showValue && (
          <div className="shrink-0 min-w-[44px] text-right text-sm font-mono tabular-nums text-[var(--text-primary)]">
            {formatValue ? formatValue(currentValue) : currentValue}
            {valueSuffix && (
              <span className="ml-0.5 text-[var(--text-muted)] text-xs">{valueSuffix}</span>
            )}
          </div>
        )}
      </div>
    );
  },
);
Slider.displayName = "Slider";
